import { ThemeEvents } from '@theme/events';

const SELECTOR =
  'cart-items-component:not([data-drawer]) .cart-page form.cart-form cart-quantity-selector-component';
const INPUT_SELECTOR = 'input[type="number"][name="updates[]"][ref="quantityInput"]';

/** @param {EventTarget | null} selector */
function getCartPageContext(selector) {
  if (!(selector instanceof HTMLElement) || !selector.isConnected || !selector.matches(SELECTOR)) return null;
  if (selector.closest('#cart-drawer, theme-drawer, dialog')) return null;

  const owner = selector.closest('cart-items-component');
  const page = selector.closest('.cart-page');
  const form = selector.closest('form.cart-form');
  const row = selector.closest('.cart-items__table-row');
  if (!(owner instanceof HTMLElement) || owner.hasAttribute('data-drawer')) return null;
  if (!(page instanceof HTMLElement) || page.closest('cart-items-component') !== owner) return null;
  if (!(form instanceof HTMLFormElement) || form.closest('.cart-page') !== page) return null;
  if (form.closest('cart-items-component') !== owner) return null;
  if (!(row instanceof HTMLElement) || row.closest('form.cart-form') !== form || !row.dataset.key) return null;

  const rowParent = row.parentElement;
  if (!rowParent) return null;
  const rowIndex = Array.from(rowParent.children).indexOf(row);

  const minusButton = selector.querySelector('button[ref="minusButton"]');
  const input = selector.querySelector(INPUT_SELECTOR);
  if (!(minusButton instanceof HTMLButtonElement) || !(input instanceof HTMLInputElement)) return null;
  if (minusButton.closest('cart-quantity-selector-component') !== selector) return null;
  if (input.closest('cart-quantity-selector-component') !== selector || input.form !== form) return null;

  const line = Number(input.dataset.cartLine);
  if (!Number.isInteger(line) || line <= 0) return null;

  return { owner, page, form, row, rowParent, rowIndex, key: row.dataset.key, line, minusButton, input };
}

/** @param {import('@theme/events').QuantitySelectorUpdateEvent} event */
function handoffFocusAfterCartPageDecrement(event) {
  if (!Number.isFinite(event.detail?.quantity) || event.detail.quantity <= 0) return;

  const selector = event.target;
  const context = getCartPageContext(selector);
  if (!context) return;
  const { minusButton, input } = context;
  if (minusButton.disabled || document.activeElement !== minusButton || !minusButton.matches(':focus-visible')) return;

  // Core dispatches the quantity event before synchronously disabling minus.
  queueMicrotask(() => {
    const current = getCartPageContext(selector);
    if (!current) return;
    if (
      current.owner !== context.owner ||
      current.page !== context.page ||
      current.form !== context.form ||
      current.row !== context.row ||
      current.rowParent !== context.rowParent ||
      current.rowIndex !== context.rowIndex ||
      current.key !== context.key ||
      current.line !== context.line ||
      current.minusButton !== minusButton ||
      current.input !== input
    ) {
      return;
    }
    if (!minusButton.isConnected || !input.isConnected || !minusButton.disabled) return;

    const currentFocus = document.activeElement;
    if (
      currentFocus !== minusButton &&
      currentFocus !== document.body &&
      currentFocus !== document.documentElement
    ) {
      return;
    }
    if (input.matches(':disabled') || input.tabIndex < 0 || input.closest('[inert], [aria-hidden="true"]')) return;
    if (getComputedStyle(input).visibility !== 'visible' || input.getClientRects().length === 0) return;

    input.focus({ preventScroll: true });
  });
}

// Delegate once so full-cart section morphs need no listener reattachment.
document.addEventListener(ThemeEvents.quantitySelectorUpdate, handoffFocusAfterCartPageDecrement);
